import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useDemo } from "@/context/DemoContext";
import { scrollToId } from "@/hooks/useLenis";

const LINKS = [
  { label: "Company", id: "company" },
  { label: "Platform", id: "platform" },
  { label: "Vision", id: "vision" },
  { label: "Products", id: "ecosystem" },
  { label: "Contact", id: "footer" },
];

export function Navbar() {
  const { launchDemo } = useDemo();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#040405]/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          onClick={() => go("hero")}
          className="focus:outline-none"
          data-testid="nav-logo-btn"
          aria-label="Cogentis Technologies home"
        >
          <Logo height={30} />
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="font-body text-sm text-white/60 transition-colors hover:text-white"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={launchDemo}
            data-testid="nav-launch-demo-btn"
            className="group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
            style={{
              background: "var(--accent)",
              boxShadow: "0 0 24px var(--accent-glow)",
            }}
          >
            Launch Demo
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>

        <button
          className="text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-[#040405]/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-body text-base text-white/80 transition-colors hover:text-white"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  launchDemo();
                }}
                data-testid="nav-mobile-launch-demo-btn"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white"
                style={{ background: "var(--accent)" }}
              >
                Launch Demo <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
